/**
 * La Coochie review stars → Judge.me reviews (blocks/lc-review-link.liquid).
 *
 * - Tapping the stars scrolls to the Judge.me widget. Smooth unless reduced motion; if WebKit leaves the smooth scroll
 *   stuck (it does after a page load that had a #fragment — see lc-faq.js), an instant scroll finishes the job.
 * - Judge.me's "next page" is a fresh request to cdn.judge.me that takes 2–4 s whenever their CDN has not cached that
 *   page yet (2026-10-12: page 2 ≈ 0.15 s HIT, page 3 ≈ 3.3 s MISS, same on Debut-era live). The responses are
 *   `Cache-Control: public, max-age=1200`, so as soon as the widget fetches page N we fetch page N+1 with the very same
 *   URL in the background; the widget's own request for it is then answered from the browser cache. Watches the
 *   widget's requests through PerformanceObserver — nothing of Judge.me's script is touched; if they change the URL
 *   shape, this simply prefetches nothing.
 */
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const WIDGET_REQUEST = /^https:\/\/cdn\.judge\.me\/reviews\/reviews_for_widget\?/;

class LcReviewLink extends Component {
  /** @param {MouseEvent} event */
  jump(event) {
    const target = document.getElementById(this.dataset.target ?? '');
    if (!target) return; // no widget on this page: let the link do whatever the browser does
    event.preventDefault();
    const scroller = target.closest('.page-wrapper');
    const before = scroller?.scrollTop ?? document.scrollingElement?.scrollTop ?? 0;
    const smooth = !prefersReducedMotion();
    target.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'instant' });
    if (smooth) {
      setTimeout(() => {
        const now = scroller?.scrollTop ?? document.scrollingElement?.scrollTop ?? 0;
        if (now === before) target.scrollIntoView({ block: 'start', behavior: 'instant' });
      }, 250);
    }
  }
}

if (!customElements.get('lc-review-link')) customElements.define('lc-review-link', LcReviewLink);

/** Highest page number in the widget's pager, or Infinity while it is unknown. */
const lastPage = () => {
  const pages = [...document.querySelectorAll('.jdgm-paginate__page[data-page]')].map((a) => Number(/** @type {HTMLElement} */ (a).dataset.page) || 0);
  return pages.length ? Math.max(...pages) : Infinity;
};

/** URLs already warmed (never twice). @type {Set<string>} */
const warmed = new Set();
/** Our own warm-up requests not yet seen by the observer: their resource entry must not chain a warm of the page after. @type {Set<string>} */
const ours = new Set();

/** @param {string} url */
const warmNext = (url) => {
  if (!WIDGET_REQUEST.test(url)) return;
  const next = new URL(url);
  const page = Number(next.searchParams.get('page'));
  if (!page || page + 1 > lastPage()) return;
  next.searchParams.set('page', String(page + 1));
  const key = next.toString();
  if (warmed.has(key)) return;
  warmed.add(key);
  ours.add(key);
  fetch(key, { mode: 'cors', credentials: 'omit', priority: 'low' }).catch(() => warmed.delete(key));
};

/**
 * Resource entries of the widget's requests (our own warm-ups excluded, so they never chain a warm of the page after).
 * @param {PerformanceObserverEntryList} entries
 */
function onResources(entries) {
  entries.getEntries().forEach((entry) => {
    if (!ours.delete(entry.name)) warmNext(entry.name);
  });
}

if (!window.lcReviewsWarm && 'PerformanceObserver' in window) {
  window.lcReviewsWarm = true;
  new PerformanceObserver(onResources).observe({ type: 'resource', buffered: true });
}
